import { Link, useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  Avatar,
  Link as FluentLink,
  tokens,
  makeStyles,
  mergeClasses,
  Subtitle2,
  Body1,
  Caption1,
  Tooltip,
  type AvatarProps,
} from '@fluentui/react-components';
import { EditFilled, OpenRegular } from '@fluentui/react-icons';
import { ControlledTooltip } from '@shared/components/ControlledTooltip';
import { AgoDateLabel } from '@shared/components/dates';
import { ItemActionsMenu } from '@shared/components/ItemActionsMenu';
import type { TItemCardStatus } from '@modules/items/types/folders.types';
import type { TAction, TItem } from '@modules/items/types/items.types';
import { SQUARE_DOUBLE_CARD_HEIGHT } from '@shared/components/Square.constants';
import { useSquareCardStyles } from '@shared/components/Square.styles';
import ItemCardStatusBadges from '@shared/components/ItemCardStatusBadges';

const useStyles = makeStyles({
  card: {
    height: SQUARE_DOUBLE_CARD_HEIGHT,
    padding: '0 !important',
  },
  header: {
    backgroundColor: tokens.colorBrandBackground,
    padding: tokens.spacingHorizontalL,
  },
  title: {
    color: tokens.colorNeutralBackground1,
    '&:hover': { textDecoration: 'underline' },
    display: 'block',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase400, // Explicitly set for consistency
  },
  avatar: {
    backgroundColor: tokens.colorNeutralBackground1,
    fontSize: '1.05rem',
    '& .fui-Avatar__icon': {
      color: tokens.colorBrandForeground1,
    },
  },
  menuBtn: {
    color: tokens.colorNeutralForegroundOnBrand,
    '&:hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
      color: tokens.colorNeutralForegroundOnBrand,
    },
    '&:hover:active': {
      backgroundColor: tokens.colorBrandBackgroundPressed,
      color: tokens.colorNeutralForegroundOnBrand,
    },
    '&:hover .fui-Button__icon': {
      color: tokens.colorNeutralForegroundOnBrand,
    },
    '&:hover:active .fui-Button__icon': {
      color: tokens.colorNeutralForegroundOnBrand,
    },
  },
  stat: {
    borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  statLabel: {
    color: tokens.colorNeutralForeground2,
  },
  footer: {
    position: 'absolute',
    right: tokens.spacingHorizontalL,
    bottom: tokens.spacingVerticalL,
    left: tokens.spacingHorizontalL,
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  lastEdited: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    whiteSpace: 'nowrap',
  },
});

export type TRootStat = {
  label: string;
  value?: number;
};

type TProps = {
  item: TItem;
  href: string;
  stats: TRootStat[];
  // Shown after "Open"; without actions the card has no menu.
  actions?: TAction[];
  avatarIcon?: AvatarProps['icon'];
  cardStatus?: TItemCardStatus;
};

const RootStatsCard = ({ item, href, stats, actions, avatarIcon, cardStatus }: TProps) => {
  const navigate = useNavigate();

  const availableActions: TAction[] | undefined = actions && [
    {
      children: 'Open',
      icon: <OpenRegular />,
      order: 0,
      enable: () => true,
      show: () => true,
      onClick: () => navigate(href),
    },
    ...actions,
  ];

  const classes = useStyles();
  const squareClasses = useSquareCardStyles();

  return (
    <Link to={href} className="h-fit w-fit">
      <Card className={mergeClasses(squareClasses.card, classes.card)}>
        <CardHeader
          className={classes.header}
          image={
            <Avatar
              initials={avatarIcon ? undefined : item.name.trim()[0].toUpperCase()}
              icon={avatarIcon}
              size={40}
              shape="square"
              className={classes.avatar}
              aria-label={item.name}
            />
          }
          header={
            <ControlledTooltip
              enable={item.name.length > 20 /* the title is one line */}
              relationship="label"
              content={item.name}
              withArrow
            >
              <FluentLink appearance="subtle">
                <span className={classes.title}>{item.name}</span>
              </FluentLink>
            </ControlledTooltip>
          }
          action={
            availableActions && (
              <ItemActionsMenu
                item={item}
                actions={availableActions}
                variant="card"
                triggerClassName={classes.menuBtn}
              />
            )
          }
        />
        <div className="flex flex-col w-full px-4 pt-4">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className={mergeClasses('flex items-baseline justify-between gap-3 py-2', classes.stat)}
            >
              <Body1 className={classes.statLabel}>{stat.label}</Body1>
              <Subtitle2 as="span">{stat.value ?? '-'}</Subtitle2>
            </div>
          ))}
          <div className={classes.footer}>
            <ItemCardStatusBadges cardStatus={cardStatus} />
            <Tooltip relationship="description" content="Last edited">
              <div className={classes.lastEdited}>
                <EditFilled />
                <Caption1>
                  <AgoDateLabel short>{item.stats.modifiedDate}</AgoDateLabel>
                </Caption1>
              </div>
            </Tooltip>
          </div>
        </div>
      </Card>
    </Link>
  );
};

export default RootStatsCard;
